import {
  ArrowBack,
  InfoOutlined,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import {
  Button,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Radio,
  Tooltip,
} from "@mui/material";
import cx from "classnames";
import { debounce, startCase } from "lodash";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ReactComponent as ArrowDown } from "assets/arrow-down.svg";
import { ReactComponent as FolderHeartPurple } from "assets/folder-heart-purple.svg";
import { ReactComponent as Seperator } from "assets/line.svg";
import { ReactComponent as SearchIcon } from "assets/search.svg";
import DotElastic from "components/DotElastic";
import SlapshotInfiniteScroll from "components/SlapshotInfiniteScroll";
import { SERVICES, TIME_RANGE } from "constants/constants";
import { SLAPSHOT_DOWNLOADS } from "constants/urls";

import styles from "./Downloads.module.scss";
import { Export } from "../../models/Export";
import { getExports, setLastDownload } from "../../services/ExportService";
import {
  ACTION_SUBSCRIBE_EXPORT_UPDATE,
  getRequestData,
  getSocket,
  closeSocket,
} from "../../services/WebSocketService";
import {
  getRelativeDate,
  getUser,
  getServiceDisplayName,
} from "../../utils/helper";

interface StatusDetails {
  newStatus: string;
  iconUrl: string;
  style: string;
  disabled: boolean;
}

const getStatusDetails = (e: Export) => {
  let obj: StatusDetails = {
    newStatus: "Download",
    style: styles.statusDownload,
    disabled: false,
    iconUrl: "/download.svg",
  };

  switch (e.status) {
    case "FAILED":
      obj = {
        newStatus: "Failed",
        iconUrl: "/failed.gif",
        style: styles.statusFailed,
        disabled: true,
      };
      break;
    case "IN_PROGRESS":
      obj = {
        newStatus: e.is_autopilot_export
          ? `${e.percent_complete}% complete...`
          : "Processing...",
        style: styles.statusProcessing,
        disabled: true,
        iconUrl: "/processing.gif",
      };
      break;
    // no default
  }
  return obj;
};

const handleDownload = async (
  e: ExportDetail,
  setExports: Dispatch<SetStateAction<{ [id: number]: ExportDetail }>>,
  type?: string,
) => {
  const res = await setLastDownload(e.id, type);
  if (res?.url) {
    window.open(res.url, "_blank");
  } else {
    toast.info("Export cannot be downloaded");
  }

  setExports((v) => ({
    ...v,
    [e.id]: { ...e, is_downloaded: res?.url ? true : e.is_downloaded },
  }));
};

interface ExportDetail extends Export, StatusDetails {}

const Downloads = () => {
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [exports, setExports] = useState<{ [id: number]: ExportDetail }>({});
  const [newDownloads, setNewDownloads] = useState(false);

  const [selectedServices, setSelectedServices] = useState<SERVICES[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TIME_RANGE>(
    TIME_RANGE.all_time,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const socket = getSocket("export");
    if (!socket) {
      return () => {};
    }

    socket.onopen = (event) => {
      socket.send(getRequestData(ACTION_SUBSCRIBE_EXPORT_UPDATE));
    };

    socket.onmessage = (event) => {
      const e: Export = JSON.parse(event.data).data;
      setExports((v) => {
        v[e.id] = { ...e, ...getStatusDetails(e) };
        return { ...v };
      });
    };

    return () => {
      closeSocket("export");
    };
  }, []);

  const fetchExports = useRef(
    debounce((term, services, timeRange, nextPage) => {
      getExports(term, services, timeRange, nextPage)
        .then((res) => {
          setHasNextPage(!!res.next);
          if (nextPage === 1) {
            const data = res.results.reduce((obj, item) => {
              obj[item.id] = { ...item, ...getStatusDetails(item) };
              return obj;
            }, {});
            setExports(data);
          } else {
            setExports((prevExports) => {
              const newExports = res.results.reduce((obj, item) => {
                obj[item.id] = { ...item, ...getStatusDetails(item) };
                return obj;
              }, {});
              return {
                ...prevExports,
                ...newExports,
              };
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
          setLoadingMore(false);
        });
    }, 500),
  ).current;

  const loadMoreExports = () => {
    if (hasNextPage && !loadingMore) {
      setLoadingMore(true);
      const nextPage = page + 1;
      setPage(nextPage);
      fetchExports(searchTerm, selectedServices, selectedTimeRange, nextPage);
    }
  };

  useEffect(() => {
    if (page === 1) setIsLoading(true);
    fetchExports.cancel();
    fetchExports(searchTerm, selectedServices, selectedTimeRange, page);
  }, [fetchExports, searchTerm, selectedServices, selectedTimeRange, page]);

  useEffect(() => {
    const newExport = Object.values(exports).findIndex(
      (value) => !value.is_downloaded && value.status === "COMPLETED",
    );
    setNewDownloads(newExport !== -1);
  }, [exports]);

  return (
    <div>
      <div>
        <Button
          className={cx(
            styles.navButton,
            location.pathname === SLAPSHOT_DOWNLOADS && styles.navButtonActive,
          )}
          onClick={() => {
            navigate(SLAPSHOT_DOWNLOADS);
          }}
        >
          <div className={styles.DownloadsBtn}>
            <div>Exports</div>
            {newDownloads && (
              <div className={styles.redDot} style={{ marginLeft: 0 }} />
            )}
          </div>
        </Button>
        {location.pathname === SLAPSHOT_DOWNLOADS && (
          <div className={styles.DownloadsPage}>
            <div className={styles.header}>
              <div className={styles.headerTitle}>
                <div
                  className={styles.back}
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <ArrowBack fontSize="medium" />
                </div>

                <h2>Exports</h2>
              </div>
              <div className={styles.filters}>
                <div className={styles.exportFilterOptions}>
                  <h5>Filter:</h5>
                  <Select
                    multiple
                    className={styles.sortingOptionsDropdown}
                    value={selectedServices}
                    onChange={(e) => {
                      setSelectedServices(e.target.value as SERVICES[]);
                    }}
                    IconComponent={ArrowDown as React.ElementType}
                    displayEmpty
                    renderValue={() => "By Service"}
                    MenuProps={{
                      slotProps: {
                        paper: {
                          sx: {
                            marginTop: "4px",
                            marginLeft: "-40px",
                            backgroundColor: "#22242A",
                            borderRadius: "7px",
                            border: "1px solid #CCCCDC1F",
                            padding: "8px",
                            boxShadow: "0px 8px 24px 0px #010409",
                            color: "white",
                          },
                        },
                      },
                    }}
                    sx={{
                      display: "flex",
                      gap: "8px",
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      },
                      "& .MuiSelect-icon": {
                        position: "relative",
                        alignSelf: "center",
                        top: "2px",
                      },
                    }}
                  >
                    {Object.values(SERVICES).map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        className={styles.menuItem}
                        sx={{
                          borderRadius: "4px",
                          padding: "5px, 0px",
                          display: "flex",
                          gap: "8px",
                          "&.Mui-selected": {
                            background: "none",
                          },
                        }}
                      >
                        <Checkbox
                          sx={{
                            color: "#CCCCDCA6",
                            "&.Mui-checked": {
                              color: "#6565ec",
                            },
                          }}
                          className={styles.checkbox}
                          checked={selectedServices.includes(option)}
                        />
                        <ListItemText primary={option.replace(/_/g, " ")} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className={styles.exportFilterOptions}>
                  <h5>Filter:</h5>
                  <Select
                    className={styles.sortingOptionsDropdown}
                    value={selectedTimeRange}
                    onChange={(e) => {
                      setSelectedTimeRange(e.target.value as TIME_RANGE);
                    }}
                    IconComponent={ArrowDown as React.ElementType}
                    displayEmpty
                    renderValue={() => "Time Duration"}
                    MenuProps={{
                      slotProps: {
                        paper: {
                          sx: {
                            marginTop: "4px",
                            marginLeft: "-30px",
                            backgroundColor: "#22242A",
                            borderRadius: "7px",
                            border: "1px solid #CCCCDC1F",
                            padding: "8px",
                            boxShadow: "0px 8px 24px 0px #010409",
                            color: "white",
                          },
                        },
                      },
                    }}
                    sx={{
                      height: "17px",
                      display: "flex",
                      gap: "8px",
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      },
                      "& .MuiSelect-icon": {
                        position: "relative",
                        alignSelf: "center",
                        top: "2px",
                      },
                    }}
                  >
                    {Object.values(TIME_RANGE).map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        className={styles.menuItem}
                        onClick={() => {
                          setSelectedTimeRange(option as TIME_RANGE);
                        }}
                        sx={{
                          borderRadius: "4px",
                          padding: "5px, 0px",
                          display: "flex",
                          gap: "8px",
                          "&.Mui-selected": {
                            background: "none",
                          },
                        }}
                      >
                        <Radio
                          sx={{
                            color: "#CCCCDCA6",
                            "&.Mui-checked": {
                              color: "#6565ec",
                            },
                          }}
                          className={styles.checkbox}
                          checked={selectedTimeRange === option}
                        />
                        <ListItemText primary={startCase(option)} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div className={styles.searchContainer}>
              <div className={styles.searchBar}>
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search by shot name..."
                  className={styles.input}
                  onChange={(e) => {
                    const term = e.target.value.trim();
                    if (term !== searchTerm) {
                      setSearchTerm(term);
                    }
                  }}
                  ref={inputRef}
                />
              </div>
            </div>

            {isLoading && (
              <div className={styles.loaderContainer}>
                <h4>Loading Exports...</h4>
                <DotElastic />
              </div>
            )}
            {!isLoading && (
              <div className={styles.exportsContainer}>
                <SlapshotInfiniteScroll
                  hasNextPage={hasNextPage}
                  loadMore={loadMoreExports}
                  threshold={300}
                  className={styles.container}
                >
                  <>
                    {Object.values(exports)
                      .sort((e, e2) => (e.created > e2.created ? -1 : 1))
                      .map((e: ExportDetail) => {
                        return (
                          <div className={styles.exportContainer} key={e.id}>
                            <div className={styles.exportContent}>
                              <FolderHeartPurple
                                className={styles.projectIcon}
                              />

                              <div className={styles.exportDetails}>
                                <div className={styles.firstRow}>
                                  <p className={styles.projectName}>
                                    {e.shots.length > 1
                                      ? e.project.name
                                      : e.shots?.[0] || ""}
                                  </p>
                                  <p className={styles.serviceTab}>
                                    {getServiceDisplayName(e.service)}
                                  </p>
                                </div>

                                <p className={styles.annotations}>
                                  {e.shots.length === 1 &&
                                    `${e.project.name}:  `}
                                  {e.shots.length > 1
                                    ? e.shots.join(", ")
                                    : e.layers.join(", ")}
                                </p>

                                <p className={styles.lastDownloadedAt}>
                                  {`${getRelativeDate(e.created, "Generated")}`}
                                </p>
                              </div>
                            </div>
                            {e.is_autopilot_export &&
                            e.status === "COMPLETED" ? (
                              <div className={styles.downloadBtnsCon}>
                                <Button
                                  disabled={e.disabled}
                                  className={cx(
                                    styles.downloadBtn,
                                    styles.autopilotDownloadBtn,
                                  )}
                                  onClick={() => {
                                    handleDownload(e, setExports, "hard");
                                  }}
                                >
                                  <div className={styles.statusIconCon}>
                                    <img
                                      src={e.iconUrl}
                                      alt=""
                                      className={cx(e.style, styles.statusIcon)}
                                    />
                                  </div>

                                  <Seperator className={styles.seperator} />

                                  <div>{e.newStatus} Hard Matte</div>

                                  {e.status === "COMPLETED" &&
                                    !e.is_downloaded && (
                                      <div className={styles.redDot} />
                                    )}
                                </Button>

                                <Button
                                  disabled={e.disabled}
                                  className={cx(
                                    styles.downloadBtn,
                                    styles.autopilotDownloadBtn,
                                  )}
                                  onClick={() => {
                                    handleDownload(e, setExports, "mb");
                                  }}
                                >
                                  <div className={styles.statusIconCon}>
                                    <img
                                      src={e.iconUrl}
                                      alt=""
                                      className={cx(e.style, styles.statusIcon)}
                                    />
                                  </div>

                                  <Seperator className={styles.seperator} />

                                  <div>{e.newStatus} Motion Blur Matte</div>

                                  {e.status === "COMPLETED" &&
                                    !e.is_downloaded && (
                                      <div className={styles.redDot} />
                                    )}
                                </Button>
                              </div>
                            ) : (
                              <div className={styles.downloadBtnsCon}>
                                <Button
                                  disabled={e.disabled}
                                  className={styles.downloadBtn}
                                  onClick={() => {
                                    handleDownload(e, setExports);
                                  }}
                                >
                                  <div className={styles.statusIconCon}>
                                    <img
                                      src={e.iconUrl}
                                      alt=""
                                      className={cx(e.style, styles.statusIcon)}
                                    />
                                  </div>

                                  <Seperator className={styles.seperator} />

                                  <div>{e.newStatus}</div>

                                  {e.status === "COMPLETED" &&
                                    !e.is_downloaded && (
                                      <div className={styles.redDot} />
                                    )}
                                </Button>
                                {e.is_autopilot_export &&
                                  e.status === "IN_PROGRESS" && (
                                    <Tooltip
                                      arrow
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            padding: "0px",
                                            backgroundColor: "unset",
                                            fontFamily: "Inter",
                                          },
                                        },
                                        arrow: {
                                          sx: {
                                            color: "#22242A",
                                          },
                                        },
                                      }}
                                      placement="top"
                                      title={
                                        <div className={styles.tooltip}>
                                          Autopilot exports tend to take longer
                                          than regular exports. We will notify
                                          you as soon as it is completed
                                        </div>
                                      }
                                    >
                                      <InfoOutlined
                                        className={styles.infoIcon}
                                      />
                                    </Tooltip>
                                  )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {hasNextPage && (
                      <div className={styles.loadingIndicator}>
                        {loadingMore
                          ? "Loading more exports..."
                          : "Scroll to load more exports"}
                      </div>
                    )}
                  </>
                </SlapshotInfiniteScroll>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Downloads;
